import type { JSONSchema7 } from 'json-schema';
import { GlobalThemeMapping } from '../../themes';
import { POLL_THEME_VARIABLES } from '../../themes/themeVariables';

export const POLL_THEME_MAPPING: GlobalThemeMapping<
  typeof POLL_THEME_VARIABLES.colors,
  typeof POLL_THEME_VARIABLES.others,
  typeof POLL_THEME_VARIABLES.fonts
> = {
  colors: {
    primary: '--tui-text-primary',
    primary_on_accent: '--tui-text-primary-on-accent-1',
    secondary: '--tui-text-secondary',
    tertiary: '--tui-text-tertiary',
    background_base: '--tui-background-elevation-1',
    background_base_alt: '--tui-background-base-alt',
    background_neutral_1: '--tui-background-neutral-1',
    background_neutral_1_hover: '--tui-background-neutral-1-hover',
    background_neutral_2: '--tui-background-neutral-2',
    background_evaluation_1: '--tui-background-elevation-2',
    action: '--tui-text-action',
    action_hover: '--tui-text-action-hover',
    accent: '--tui-background-accent-1',
    accent_hover: '--tui-background-accent-1-hover',
    accent_active: '--tui-background-accent-1-pressed',
    accent_2: '--tui-background-accent-2',
    accent_2_hover: '--tui-background-accent-2-hover',
    accent_2_active: '--tui-background-accent-2-pressed',
    negative: ['--tui-text-negative', '--tui-status-negative'],
    negative_pale: '--tui-status-negative-pale',
    negative_pale_hover: '--tui-status-negative-pale-hover',
    positive: ['--tui-text-positive', '--tui-status-positive'],
    positive_pale: '--tui-status-positive-pale',
    info_pale: '--tui-status-info-pale',
    warning_pale: '--tui-status-warning-pale',
    progress_bar_line: '--custom-tui-progress-bar-line',
    progress_bar_line_filled: '--custom-tui-progress-bar-line-filled',
    border_normal: '--tui-border-normal',
    border_focus: '--tui-border-focus',
    border_hover: '--tui-border-hover',
  },
  others: {
    survey_elements_border_radius: '--custom-survey-elements-border-radius',
    survey_container_border_radius_desktop: '--custom-survey-container-border-radius-desktop',
    survey_container_border_radius_mobile: '--custom-survey-container-border-radius-mobile',
    survey_container_inner_padding_desktop: '--custom-survey-container-inner-padding-desktop',
    survey_container_inner_padding_mobile: '--custom-survey-container-inner-padding-mobile',
  },
  fonts: {
    font_heading_survey_title: '--tui-font-heading-3',
    font_heading_page_title: '--tui-font-heading-5',
    font_text_body: '--tui-font-text-xl-bold',
    font_text_main: '--tui-font-text-m',
    font_text_hints: '--tui-font-text-s',
    font_text_micro_hints: '--tui-font-text-xs',
    font_text_main_ui: '--tui-font-text-ui-m',
  },
  custom: {
    '--custom-tui-progress-bar-line-filled': 'var(--tui-background-neutral-4)',
    '--custom-tui-progress-bar-line': 'var(--tui-background-neutral-3)',
    '--custom-survey-elements-border-radius': '8px',
    '--custom-survey-container-border-radius-desktop': '24px',
    '--custom-survey-container-border-radius-mobile': '16px',
    '--custom-survey-container-inner-padding-desktop': '32px',
    '--custom-survey-container-inner-padding-mobile': '20px',
    '--custom-tui-iframe-border': 'var(--tui-border-normal)',
  },
} as const;

export const createThemePollColorsSchema = (): JSONSchema7 => {
  const properties: Record<string, JSONSchema7> = {};

  for (const key of Object.keys(POLL_THEME_VARIABLES.colors)) {
    const description = POLL_THEME_VARIABLES.colors[key as keyof typeof POLL_THEME_VARIABLES.colors].description;

    properties[key] = {
      type: 'object',
      description: description || ``,
      properties: {
        light: { type: 'string' },
        dark: { type: 'string' },
      },
      required: ['light', 'dark'],
      additionalProperties: false,
    };
  }

  return {
    type: 'object',
    description:
      'Абстрактно объявленные названия цветов которые ссылаются на tui цветовую палитру, отношения к цвету TUI one-to-many',
    properties,
    additionalProperties: false,
    required: Object.keys(POLL_THEME_VARIABLES.colors),
  };
};

export const FONT_FAMILIES = [
  'Factor A',
  'haas',
  'pfhighway',
  'dsText',
  'dsHeading',
  'Roboto Flex',
  'Montserrat',
  'Graphik',
] as const;

export type ThemeFontFamilies = (typeof FONT_FAMILIES)[number];

export const createPollThemeFontsSchema = (): JSONSchema7 => {
  const fontFamilySchema: JSONSchema7 = {
    type: 'object',
    description: 'Fonts used for headings and main text',
    properties: {
      heading_font_family: {
        description: 'Font Family всех заголовков в опросе',
        enum: FONT_FAMILIES,
      },
      text_font_family: {
        description: 'Font Family основного текста в опросе, все кроме заголовков',
        enum: FONT_FAMILIES,
      },
    },
    additionalProperties: false,
    required: ['heading_font_family', 'text_font_family'],
  };

  const fontsVariablesSchema: Record<string, JSONSchema7> = {};

  for (const key of Object.keys(POLL_THEME_VARIABLES.fonts) as Array<keyof typeof POLL_THEME_VARIABLES.fonts>) {
    const description = POLL_THEME_VARIABLES.fonts[key].description;

    fontsVariablesSchema[key] = {
      type: 'object',
      description: description || '',
      properties: {
        desktop: {
          type: 'object',
          description: 'Шрифты для десктопа',
          properties: {
            fontWeight: { type: 'string' },
            fontSize: { type: 'string' },
            lineHeight: { type: 'string' },
          },
          required: ['fontWeight', 'fontSize', 'lineHeight'],
          additionalProperties: false,
        },
        mobile: {
          type: 'object',
          description: 'Шрифты для мобильных устройств',
          properties: {
            fontWeight: { type: 'string' },
            fontSize: { type: 'string' },
            lineHeight: { type: 'string' },
          },
          required: ['fontWeight', 'fontSize', 'lineHeight'],
          additionalProperties: false,
        },
      },
      required: ['desktop', 'mobile'],
      additionalProperties: false,
    };
  }

  return {
    type: 'object',
    properties: {
      font_family: fontFamilySchema,
      font_variables: {
        type: 'object',
        properties: { ...fontsVariablesSchema },
        required: Object.keys(fontsVariablesSchema),
      },
    },
    additionalProperties: false,
    required: ['font_family', 'font_variables'],
  };
};

export const createPollThemeOthersSchema = () => {
  const properties: Record<string, JSONSchema7> = {};

  for (const key of Object.keys(POLL_THEME_VARIABLES.others)) {
    const description = POLL_THEME_VARIABLES.others[key as keyof typeof POLL_THEME_VARIABLES.others]?.description;

    properties[key] = {
      description: description || ``,
      type: 'string',
    };
  }

  return {
    type: 'object',
    description: 'Дополнительные параметры стилей для опроса',
    properties,
    additionalProperties: false,
    required: Object.keys(POLL_THEME_VARIABLES.others),
  };
};

export const createPollThemeLogotypeSchema = (): JSONSchema7 => {
  const properties: Record<string, JSONSchema7> = {};

  for (const key of Object.keys(POLL_THEME_VARIABLES.logotype) as Array<keyof typeof POLL_THEME_VARIABLES.logotype>) {
    const description = POLL_THEME_VARIABLES.logotype[key].description;

    if (key === 'logotype_link') {
      properties[key] = {
        type: 'string',
        description: description || '',
        format: 'uri',
      };
    } else {
      properties[key] = {
        type: 'object',
        description: description || '',
        properties: {
          light: {
            type: 'string',
            description: 'Источник логотипа для светлой темы',
            format: 'uri',
          },
          dark: {
            type: 'string',
            description: 'Источник логотипа для тёмной темы',
            format: 'uri',
          },
        },
        additionalProperties: false,
      };
    }
  }

  return {
    type: 'object',
    description: 'Настройка логотипа при открытии опроса на poll site',
    properties,
    additionalProperties: false,
  };
};
